import React, { useState, useEffect, useRef } from 'react';
import read from '../../assets/read.svg';
import { motion, AnimatePresence } from 'framer-motion';

interface Notification {
  type: string;
  title: string;
  time: string;
  isRead?: boolean;
}

interface NotificationPanelProps {
  notifications: Notification[];
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ notifications, onClose }) => {
  const [filter, setFilter] = useState('all');
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const getFilteredNotifications = () => {
    switch (filter) {
      case 'unread':
        return notifications.filter(n => !n.isRead);
      case 'recent':
        return [...notifications].sort((a, b) => 
          new Date(b.time).getTime() - new Date(a.time).getTime()
        );
      default:
        return notifications;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={panelRef}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="absolute top-7 -right-4 w-[420px] h-[520px] bg-white rounded-[16px] shadow-lg border border-[#EEEEEE] z-50"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-[#EEEEEE]">
            <h2 className="text-[16px] font-semibold text-[#222222]">Notifications</h2>
          </div>

          {/* Filter Options */}
          <div className="flex items-center gap-4 p-4 border-b border-[#EEEEEE]">
            <button 
              onClick={() => setFilter('unread')}
              className="text-sm font-medium rounded-[8px] px-2 py-1 border border-[#E6E7EA] text-[#000000]"
            >
              <span className="text-[#1565C0] mr-1">•</span> 
              <span className="text-[#000000]">{unreadCount} unread</span>
            </button>
            <button 
              onClick={() => setFilter('recent')}
              className="text-sm font-medium rounded-[8px] px-2 py-1 border border-[#E6E7EA] text-[#000000]"
            >
              Recent
            </button>
            <div className="flex-grow" />
            <button className="text-sm text-[#75777C] flex items-center gap-1">
              <img src={read} alt="Mark all as read" />
              Mark all as read
            </button>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {getFilteredNotifications().map((notification, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-3 hover:bg-[#FAFAFA] cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full bg-[#E8FFF0] flex items-center justify-center">
                  {notification.type.charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-[#222222]">{notification.title}</p>
                  <span className="text-xs text-[#75777C]">{notification.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPanel; 